import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import {
  FileText,
  Layers,
  Droplets,
  ScanLine,
  Ship,
  Factory,
  Building2,
} from 'lucide-react';
import { fetchCustomGrading } from '../lib/sanity';
import { defaultCustomGrading } from '../lib/defaultContent';
import type {
  CustomGradingContent,
  CustomGradingUseCase,
  CustomGradingStep,
  GradeTier,
} from '../lib/types';

const iconMap = {
  Ship,
  Factory,
  Building2,
  ScanLine,
  Layers,
  Droplets,
  FileText,
};

const gradeColors: Record<string, string> = {
  Premium: 'bg-secondary-sage/15 text-secondary-sage',
  'Grade 1': 'bg-secondary-sage/15 text-secondary-sage',
  'Grade A': 'bg-secondary-sage/15 text-secondary-sage',
  Standard: 'bg-secondary-terracotta/15 text-secondary-terracotta',
  'Grade 2': 'bg-secondary-terracotta/15 text-secondary-terracotta',
  'Grade B': 'bg-secondary-terracotta/15 text-secondary-terracotta',
  Processing: 'bg-gray-100 text-gray-600',
};

const CustomGrading: React.FC = () => {
  const [content, setContent] = useState<CustomGradingContent>(defaultCustomGrading);

  useEffect(() => {
    fetchCustomGrading().then((data) => {
      if (data) setContent({ ...defaultCustomGrading, ...data });
    });
  }, []);

  const useCases: CustomGradingUseCase[] = content.useCases || [];
  const gradingSteps: CustomGradingStep[] = content.steps || [];
  const gradeTiers: GradeTier[] = content.tiers || [];

  return (
    <main className="pt-20">
      <Section padding="default">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-playfair font-bold text-primary-black mb-4">
            <span className="text-primary-red">{content.title}</span> {content.titleHighlight}
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-8">
            {content.subtitle}
          </p>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            {content.intro}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 mb-16">
          <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
            <img
              src={content.images?.[0] || defaultCustomGrading.images[0]}
              alt="Grading and sorting equipment"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
            <img
              src={content.images?.[1] || defaultCustomGrading.images[1]}
              alt="Processing machinery in operation"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        {/* Who It's For */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary-black mb-3">
              {content.whoTitle}
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl">
              {content.whoDescription}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => {
              const IconComponent =
                iconMap[useCase.icon as keyof typeof iconMap] || Ship;
              return (
                <Card key={index}>
                  <div className="flex flex-col h-full">
                    <div className="w-14 h-14 rounded-full bg-primary-red/10 flex items-center justify-center mb-4">
                      <IconComponent className="w-7 h-7 text-primary-red" />
                    </div>
                    <h3 className="text-xl font-playfair font-bold text-primary-black mb-3">
                      {useCase.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed flex-grow">
                      {useCase.description}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* The Grading Process */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary-black mb-4">
              {content.processTitle}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl">
              {content.processDescription}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {gradingSteps.map((step, index) => {
              const IconComponent =
                iconMap[step.icon as keyof typeof iconMap] || ScanLine;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col items-center gap-2 flex-shrink-0">
                      <div className="text-3xl font-playfair font-bold text-primary-red/20 leading-none">
                        {step.number}
                      </div>
                      <div className="w-10 h-10 rounded-full bg-secondary-sage/15 flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-secondary-sage" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-playfair font-bold text-primary-black mb-2">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Grade Tiers Per Commodity */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-primary-black mb-4">
              {content.tiersTitle}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl">
              {content.tiersDescription}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {gradeTiers.map((tier, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-cream-50 rounded-xl p-5 shadow-sm"
              >
                <h3 className="text-lg font-playfair font-bold text-primary-black mb-4 pb-3 border-b border-gray-200">
                  {tier.commodity}
                </h3>
                <div className="space-y-2">
                  {tier.grades.map((grade, i) => (
                    <span
                      key={i}
                      className={`block px-3 py-2 rounded-lg text-sm font-inter font-medium text-center ${
                        gradeColors[grade] || 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {grade}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Grade legend */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="flex flex-wrap items-center gap-6 mt-6 px-1"
          >
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-secondary-sage flex-shrink-0" />
              <span className="text-sm text-gray-600 font-inter">
                {content.legend?.[0] || defaultCustomGrading.legend[0]}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-secondary-terracotta flex-shrink-0" />
              <span className="text-sm text-gray-600 font-inter">
                {content.legend?.[1] || defaultCustomGrading.legend[1]}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-gray-300 flex-shrink-0" />
              <span className="text-sm text-gray-600 font-inter">
                {content.legend?.[2] || defaultCustomGrading.legend[2]}
              </span>
            </div>
          </motion.div>
        </div>

        {/* Documentation callout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="bg-cream-50 rounded-2xl p-8 md:p-10 border-l-4 border-primary-red">
            <h3 className="text-2xl font-playfair font-bold text-primary-black mb-3">
              {content.docTitle}
            </h3>
            <p className="text-gray-700 leading-relaxed max-w-3xl">
              {content.docBody}
            </p>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-secondary-sage/20 to-secondary-terracotta/20 rounded-2xl p-8 md:p-12"
        >
          <h3 className="text-3xl md:text-4xl font-playfair font-bold text-primary-black mb-4">
            {content.ctaTitle}
          </h3>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl leading-relaxed">
            {content.ctaBody}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button variant="primary" href={content.ctaPrimaryLink}>
              {content.ctaPrimaryText}
            </Button>
            <Button variant="secondary" href={content.ctaSecondaryLink}>
              {content.ctaSecondaryText}
            </Button>
          </div>
        </motion.div>
      </Section>
    </main>
  );
};

export default CustomGrading;
